import React, { useRef, useState, useEffect } from 'react';
import './singinture.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../Firebaseconfig';

function Singinture() {
  const navigate = useNavigate();
  const location = useLocation();
  const signupData = location.state?.signupData || null;

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const lastPos = useRef(null);

  const [businessName, setBusinessName] = useState('');
  const [businessLocation, setBusinessLocation] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [partnershipModel, setPartnershipModel] = useState('revenue_share');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!signupData) {
      navigate('/signup');
      return;
    }
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#d0d4de';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [signupData, navigate]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    setIsDrawing(true);
    setHasSignature(true);
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    lastPos.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    if (!businessName || !businessLocation || !contactNumber || !businessEmail) {
      setSubmitError('Please fill out all business details.');
      return;
    }
    if (!hasSignature) {
      setSubmitError('Please sign the document before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, signupData.email, signupData.password);
      const user = userCredential.user;

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        firstName: signupData.firstName,
        lastName: signupData.lastName,
        email: signupData.email,
        role: 'company',
        businessName,
        businessLocation,
        contactNumber,
        businessEmail,
        partnershipModel,
        hasSignature: true,
        status: 'Active',
        createdAt: new Date().toISOString()
      });

      navigate('/dashboard');
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!signupData) return null;

  return (
    <div className="sig-page">
      <div className="sig-card">

        {/* ── Open for Applications badge ── */}
        <div className="sig-badge-row">
          <span className="sig-badge">
            <span className="sig-badge-dot" />
            Open for Applications
          </span>
        </div>

        {/* ── Hero heading ── */}
        <div className="sig-hero">
          <h1 className="sig-hero-title">
            Partner with <span className="sig-brand">CraveDash</span>
          </h1>
          <p className="sig-hero-sub">
            Grow your business with our premium delivery network. Reach thousands of
            local customers and scale your operations with professional logistics.
          </p>
        </div>

        {/* ── Business Details ── */}
        <section className="sig-section">
          <h2 className="sig-section-title">Business Details</h2>
          <div className="sig-grid-2">
            <div className="sig-field">
              <label className="sig-label">Business Name</label>
              <input 
                className="sig-input" 
                type="text" 
                placeholder="e.g. The Gourmet Kitchen" 
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </div>
            <div className="sig-field">
              <label className="sig-label">Business Location</label>
              <div className="sig-input-wrap">
                <svg className="sig-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <input 
                  className="sig-input sig-input--icon" 
                  type="text" 
                  placeholder="Search address..." 
                  value={businessLocation}
                  onChange={(e) => setBusinessLocation(e.target.value)}
                />
              </div>
            </div>
            <div className="sig-field">
              <label className="sig-label">Contact Number</label>
              <input 
                className="sig-input" 
                type="tel" 
                placeholder="+1 (555) 000-0000" 
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
              />
            </div>
            <div className="sig-field">
              <label className="sig-label">Business Email</label>
              <input 
                className="sig-input" 
                type="email" 
                placeholder="partner@mybusiness.com" 
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* ── Partnership Model ── */}
        <section className="sig-section">
          <h2 className="sig-section-title">Select Partnership Model</h2>
          <p className="sig-section-sub">Choose the plan that best aligns with your business goals.</p>
          <div className="sig-plans">

            <div 
              className={`sig-plan ${partnershipModel === 'revenue_share' ? 'sig-plan--active' : ''}`}
              onClick={() => setPartnershipModel('revenue_share')}
            >
              <div className="sig-plan-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <div className="sig-plan-name">Revenue Share</div>
              <div className="sig-plan-price sig-plan-price--orange">20% Commission</div>
              <p className="sig-plan-desc">
                A flexible, performance-based model. Only pay when you sell. No upfront costs or monthly fees.
              </p>
              <ul className="sig-plan-features">
                <li>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  No Risk Setup
                </li>
                <li>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Premium Logistics Support
                </li>
              </ul>
            </div>

            <div 
              className={`sig-plan ${partnershipModel === 'licensed_partner' ? 'sig-plan--active' : ''}`}
              onClick={() => setPartnershipModel('licensed_partner')}
            >
              <div className="sig-plan-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
              </div>
              <div className="sig-plan-name">Licensed Partner</div>
              <div className="sig-plan-price sig-plan-price--teal">€500 Annual Fee</div>
              <p className="sig-plan-desc">
                Ideal for high-volume merchants. 0% commission on sales. Maximize your profit margins.
              </p>
              <ul className="sig-plan-features">
                <li>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  0% Sales Commission
                </li>
                <li>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Priority Support Channel
                </li>
              </ul>
            </div>

          </div>
        </section>

        {/* ── Digital Signature ── */}
        <section className="sig-section">
          <div className="sig-canvas-header">
            <h2 className="sig-section-title sig-section-title--inline">Digital Signature</h2>
            <button className="sig-clear-btn" onClick={clearCanvas}>Clear Canvas</button>
          </div>

          <div className="sig-canvas-wrap">
            {!hasSignature && (
              <div className="sig-canvas-overlay">
                <span className="sig-canvas-hint">SIGN HERE TO AUTHORIZE</span>
                <div className="sig-canvas-line" />
              </div>
            )}
            <canvas
              ref={canvasRef}
              className="sig-canvas"
              width={500}
              height={140}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>

          <p className="sig-disclaimer">
            By signing, you agree to CraveDash's Merchant{' '}
            <a href="#" className="sig-link">Terms of Service</a> and{' '}
            <a href="#" className="sig-link">Privacy Policy</a>.
          </p>
        </section>

        {/* ── Submit ── */}
        <div className="sig-submit-wrap">
          {submitError && <p className="sig-submit-error" style={{ color: '#ef4444', fontSize: '12px', margin: 0 }}>{submitError}</p>}
          <button 
            className="sig-submit-btn" 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
          >
            {isSubmitting ? 'Creating Account...' : 'Submit Partnership Application'}
          </button>
          <p className="sig-submit-note">Typically reviewed within 24–48 business hours.</p>
        </div>

      </div>
    </div>
  );
}

export default Singinture;